import React, { ComponentType, FC, memo, MemoExoticComponent } from 'react';

import { logger } from '.';

/**
 * Performance optimization utilities for React components
 * Provides type-safe wrappers and helpers for common optimization patterns
 */

/**
 * Type-safe wrapper for React.memo with custom comparison function
 * @param Component - The component to memoize
 * @param propsAreEqual - Optional custom comparison function
 * @returns Memoized component
 */
export function memoizeComponent<P extends object>(
  Component: ComponentType<P>,
  propsAreEqual?: (prevProps: Readonly<P>, nextProps: Readonly<P>) => boolean
): MemoExoticComponent<ComponentType<P>> {
  return memo(Component, propsAreEqual);
}

/**
 * Deep comparison function for React.memo
 * Performs deep equality check on props
 */
export function deepPropsAreEqual<P extends object>(
  prevProps: Readonly<P>,
  nextProps: Readonly<P>
): boolean {
  return deepEqual(prevProps, nextProps);
}

/**
 * Shallow comparison function that ignores functions
 * Useful for components that receive stable callbacks
 */
export function shallowPropsAreEqualIgnoreFunctions<P extends object>(
  prevProps: Readonly<P>,
  nextProps: Readonly<P>
): boolean {
  const prevKeys = Object.keys(prevProps) as (keyof P)[];
  const nextKeys = Object.keys(nextProps) as (keyof P)[];

  if (prevKeys.length !== nextKeys.length) {
    return false;
  }

  for (const key of prevKeys) {
    const prevValue = prevProps[key];
    const nextValue = nextProps[key];

    if (typeof prevValue === 'function' && typeof nextValue === 'function') {
      continue; // Skip function comparison
    }

    if (!Object.is(prevValue, nextValue)) {
      return false;
    }
  }

  return true;
}

/**
 * Comparison function that only compares specific props
 * @param keys - Array of prop keys to compare
 */
export function createSelectivePropsComparison<P extends object>(
  keys: (keyof P)[]
): (prevProps: Readonly<P>, nextProps: Readonly<P>) => boolean {
  return (prevProps, nextProps) => {
    for (const key of keys) {
      if (!Object.is(prevProps[key], nextProps[key])) {
        return false;
      }
    }
    return true;
  };
}

/**
 * Measure component render time for development
 * @param componentName - Name for logging
 */
export function measureRenderTime(componentName: string) {
  return <P extends object>(Component: ComponentType<P>): FC<P> => {
    if (process.env.NODE_ENV === 'production') {
      return Component as FC<P>;
    }

    const MeasuredComponent: FC<P> = (props) => {
      const startTime = performance.now();

      React.useEffect(() => {
        const endTime = performance.now();
        const renderTime = endTime - startTime;

        if (renderTime > 16) {
          // Longer than one frame (60fps)
          logger.warn(`[Performance] ${componentName} took ${renderTime.toFixed(2)}ms to render`);
        }
      });

      return <Component {...props} />;
    };

    MeasuredComponent.displayName = `Measured(${componentName})`;
    return MeasuredComponent;
  };
}

/**
 * Debounce render updates for frequently changing props
 * @param Component - Component to debounce
 * @param delay - Debounce delay in milliseconds
 * @param propsToDebounce - Array of prop keys to debounce
 */
export function debounceRender<P extends object>(
  Component: ComponentType<P>,
  delay: number,
  propsToDebounce: (keyof P)[]
): FC<P> {
  const DebouncedComponent: FC<P> = (props) => {
    const [debouncedProps, setDebouncedProps] = React.useState(props);
    const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    React.useEffect(() => {
      const propsChanged = propsToDebounce.some(
        (key) => !Object.is(props[key], debouncedProps[key])
      );

      if (propsChanged) {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
          setDebouncedProps(props);
        }, delay);
      }

      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }, [props, debouncedProps]);

    // Merge debounced props with current props
    const mergedProps = React.useMemo(() => {
      const result = { ...props };
      propsToDebounce.forEach((key) => {
        result[key] = debouncedProps[key];
      });
      return result;
    }, [props, debouncedProps]);

    return <Component {...mergedProps} />;
  };

  DebouncedComponent.displayName = `Debounced(${Component.displayName || Component.name})`;
  return DebouncedComponent;
}

/**
 * Batch multiple state updates to reduce renders
 * @param updates - Array of state update functions
 */
export function batchUpdates(updates: (() => void)[]): void {
  // React 18+ automatically batches updates, but this ensures it
  React.startTransition(() => {
    updates.forEach((update) => update());
  });
}

/**
 * Create a render throttler for animation-heavy components
 * @param Component - Component to throttle
 * @param fps - Target frames per second
 */
export function throttleRender<P extends object>(
  Component: ComponentType<P>,
  fps: number = 30
): FC<P> {
  const ThrottledComponent: FC<P> = (props) => {
    const [throttledProps, setThrottledProps] = React.useState(props);
    const lastUpdateRef = React.useRef(Date.now());
    const frameTimeRef = React.useRef(1000 / fps);
    const rafRef = React.useRef<number | null>(null);

    React.useEffect(() => {
      const update = () => {
        const now = Date.now();
        if (now - lastUpdateRef.current >= frameTimeRef.current) {
          setThrottledProps(props);
          lastUpdateRef.current = now;
        } else {
          rafRef.current = requestAnimationFrame(update);
        }
      };

      update();

      return () => {
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current);
        }
      };
    }, [props]);

    return <Component {...throttledProps} />;
  };

  ThrottledComponent.displayName = `Throttled(${Component.displayName || Component.name})`;
  return ThrottledComponent;
}

/**
 * Deep equality comparison utility
 */
function deepEqual(obj1: unknown, obj2: unknown): boolean {
  if (obj1 === obj2) return true;

  if (obj1 == null || obj2 == null) return false;
  if (typeof obj1 !== typeof obj2) return false;
  if (typeof obj1 !== 'object') return false;

  if (obj1 instanceof Date && obj2 instanceof Date) {
    return obj1.getTime() === obj2.getTime();
  }

  if (Array.isArray(obj1) && Array.isArray(obj2)) {
    if (obj1.length !== obj2.length) return false;
    for (let i = 0; i < obj1.length; i++) {
      if (!deepEqual(obj1[i], obj2[i])) return false;
    }
    return true;
  }

  if (typeof obj1 === 'object' && typeof obj2 === 'object') {
    const keys1 = Object.keys(obj1 as Record<string, unknown>);
    const keys2 = Object.keys(obj2 as Record<string, unknown>);

    if (keys1.length !== keys2.length) return false;

    for (const key of keys1) {
      if (!keys2.includes(key)) return false;
      if (
        !deepEqual((obj1 as Record<string, unknown>)[key], (obj2 as Record<string, unknown>)[key])
      )
        return false;
    }

    return true;
  }

  return false;
}

/**
 * Performance hints for development
 */
export const PerformanceHints = {
  /**
   * Check if a value is expensive to compute
   * @param computeFn - Function to measure
   * @param threshold - Time threshold in milliseconds
   */
  isExpensiveComputation: (computeFn: () => unknown, threshold: number = 10): boolean => {
    if (process.env.NODE_ENV === 'production') return false;

    const start = performance.now();
    computeFn();
    const duration = performance.now() - start;

    if (duration > threshold) {
      logger.warn(
        `[Performance] Expensive computation detected: ${duration.toFixed(2)}ms. Consider using useMemo.`
      );
      return true;
    }

    return false;
  },

  /**
   * Log render reason in development
   * @param componentName - Component name
   * @param prevProps - Previous props
   * @param nextProps - Next props
   */
  logRenderReason: <P extends object>(componentName: string, prevProps: P, nextProps: P): void => {
    if (process.env.NODE_ENV === 'production') return;

    const changedProps: string[] = [];

    Object.keys(nextProps).forEach((key) => {
      if (!Object.is(prevProps[key as keyof P], nextProps[key as keyof P])) {
        changedProps.push(key);
      }
    });

    if (changedProps.length > 0) {
      logger.info(`[Performance] ${componentName} re-rendered due to changed props:`, {
        changedProps,
      });
    }
  },
};
